import { useLingui } from '@lingui/react/macro';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HandymanIcon from '@mui/icons-material/Handyman';
import InsightsIcon from '@mui/icons-material/Insights';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import NotificationsIcon from '@mui/icons-material/Notifications';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PercentIcon from '@mui/icons-material/Percent';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ScienceIcon from '@mui/icons-material/Science';
import SettingsIcon from '@mui/icons-material/Settings';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import StackedLineChartRounded from '@mui/icons-material/StackedLineChartRounded';
import SwapVerticalCircleRoundedIcon from '@mui/icons-material/SwapVerticalCircleRounded';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack } from '@mui/material';
import { Link } from '@tanstack/react-router';
import { type MouseEvent, memo, type ReactNode, useCallback } from 'react';
import { BaseDrawer, Button, drawerContentSx } from 'ui-kit';
import { ReferralsMiniCard } from '../../features/referral/components/referrals-mini-card/referralsMiniCard.tsx';
import { FunCheckoutButton } from '../../providers/fun_bridge/fun_bridge.tsx';
import { useFeedbackEnabled } from '../../providers/usersnap/FeedbackButton.tsx';
import { RaisedHandIcon } from '../../providers/usersnap/raised_hand.tsx';
import useAppStore from '../../store/rootStore.ts';
import { telemetry } from '../../utils/telemetry';
import { ConnectionStatus } from '../footer/ConnectionStatus';
import { SocialMediaLinks } from '../footer/social_links';
import { CustomConnectButton as ConnectButton } from '../rainbow/CustomConnectButton.tsx';
import { ACTIVE_PROPS } from './constants';
import { LanguageDrawerSection } from './language_menu.tsx';
import { MonadNavIcon } from './monad_nav_icon.tsx';
import { useThemeToggle } from './theme_toggle.tsx';
import { useFilteredNavItems } from './useFilteredNavItems.ts';

// Keyed by `NAV_ITEMS[].value`, not label — labels change (partner rename, etc.).
const NAV_ICONS: Record<number, ReactNode> = {
  0: <SwapVerticalCircleRoundedIcon />, // Trade
  1: <StackedLineChartRounded />, // Portfolio
  2: <LeaderboardIcon />, // Leaderboard
  16: <InsightsIcon />, // Dashboard
  4: <ShoppingBagIcon />, // The Bag
  5: <EmojiEventsIcon />, // Points
  6: <PersonAddIcon />, // Referrals
  8: <MonadNavIcon />, // mPoints
  9: <MilitaryTechIcon />, // Tournaments
  10: <VpnKeyIcon />, // API Keys
  11: <ScienceIcon />, // Testnet
  12: <HandymanIcon />, // Builder Codes
  13: <MenuBookIcon />, // Docs
  14: <NewReleasesIcon />, // Changelog
  15: <PercentIcon />, // Funding Rates
};

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
  canDeposit: boolean;
  canWithdraw: boolean;
  canBridge: boolean;
  onDeposit: () => void;
  onSettingsMenuOpen: (event: MouseEvent<HTMLElement>) => void;
  showLanguageFeature: boolean;
  onNotificationsOpen: () => void;
  unreadCount: number;
  onWithdraw: () => void;
}

export const MobileDrawer = memo(function MobileDrawer({
  open,
  onClose,
  canDeposit,
  canWithdraw,
  canBridge,
  onDeposit,
  onWithdraw,
  onSettingsMenuOpen,
  showLanguageFeature,
  onNotificationsOpen,
  unreadCount,
}: MobileDrawerProps) {
  const { t } = useLingui();
  const filteredNavItems = useFilteredNavItems();
  const { toggle: toggleTheme, Icon: ThemeIcon } = useThemeToggle();
  const feedbackEnabled = useFeedbackEnabled();
  const features = useAppStore((state) => state.config?.features);

  const handleNavClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleDepositClick = useCallback(() => {
    onDeposit();
    onClose();
  }, [onDeposit, onClose]);

  const handleWithdrawClick = useCallback(() => {
    onWithdraw();
    onClose();
  }, [onWithdraw, onClose]);

  const handleNotificationsClick = useCallback(() => {
    onNotificationsOpen();
    onClose();
  }, [onNotificationsOpen, onClose]);

  const handleFeedbackClick = useCallback(() => {
    telemetry.logEvent('feedback_button_clicked');
    onClose();
  }, [onClose]);

  const handleSettingsClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      onSettingsMenuOpen(event);
      onClose();
    },
    [onSettingsMenuOpen, onClose]
  );

  return (
    <BaseDrawer
      open={open}
      onClose={onClose}
      width={320}
      keepMounted
      title={<ConnectButton />}
      closeAriaLabel={t({ comment: 'Mobile drawer close button', message: 'Close menu' })}
    >
      <Stack gap={4} sx={{ ...drawerContentSx, py: 2 }}>
        {/* === Section 1: nav + settings/theme + deposit/bridge === */}
        <Stack gap={2}>
          {/* Nav items: the full list, tabs and Tools-dropdown entries alike */}
          <List disablePadding>
            {filteredNavItems.map((item) => (
              <ListItem key={item.value} disablePadding>
                {item.external ? (
                  <ListItemButton
                    component="a"
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleNavClick}
                  >
                    <ListItemIcon>{NAV_ICONS[item.value] ?? null}</ListItemIcon>
                    <ListItemText primary={item.label} />
                    <OpenInNewIcon fontSize="xSmall" color="disabled" />
                  </ListItemButton>
                ) : (
                  <ListItemButton component={Link} to={item.path} onClick={handleNavClick} activeProps={ACTIVE_PROPS}>
                    <ListItemIcon>{NAV_ICONS[item.value] ?? null}</ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                )}
              </ListItem>
            ))}
          </List>

          {/* Notifications + Language + Settings + Theme */}
          <List disablePadding>
            <ListItem disablePadding>
              <ListItemButton onClick={handleNotificationsClick}>
                <ListItemIcon>
                  <NotificationsIcon />
                </ListItemIcon>
                <ListItemText
                  primary={
                    unreadCount > 0
                      ? t({
                          comment: 'Mobile drawer notifications item with unread count',
                          message: `Notifications (${{ unreadCount }})`,
                        })
                      : t({ comment: 'Mobile drawer notifications item', message: 'Notifications' })
                  }
                />
              </ListItemButton>
            </ListItem>
            {showLanguageFeature ? <LanguageDrawerSection /> : null}
            <ListItem disablePadding>
              <ListItemButton onClick={handleSettingsClick}>
                <ListItemIcon>
                  <SettingsIcon />
                </ListItemIcon>
                <ListItemText primary={t({ comment: 'Mobile drawer settings item', message: 'Settings' })} />
              </ListItemButton>
            </ListItem>
            {feedbackEnabled ? (
              <ListItem disablePadding>
                <ListItemButton onClick={handleFeedbackClick}>
                  <ListItemIcon>
                    <RaisedHandIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={t({ comment: 'Mobile drawer item that opens the feedback survey', message: 'Feedback' })}
                  />
                </ListItemButton>
              </ListItem>
            ) : null}
            {/* <ListItem disablePadding>
              <ListItemButton onClick={toggleTheme}>
                <ListItemIcon>
                  <ThemeIcon />
                </ListItemIcon>
                <ListItemText primary="Theme" />
              </ListItemButton>
            </ListItem> */}
          </List>

          {/* Deposit + Bridge buttons — always side by side */}
          {canDeposit || canBridge ? (
            <Stack direction="row" gap={2} px={2}>
              {canDeposit ? (
                <Button onClick={handleDepositClick} variant="contained" size="large" sx={{ flex: 1 }}>
                  {t({
                    comment:
                      'Button opening the deposit dialog. An action ("add funds"), not the noun for a deposit transaction — the uncontexted "Deposit" is the noun used by the account-history row and the table filter.',
                    context: 'action',
                    message: 'Deposit',
                  })}
                </Button>
              ) : null}
              {canBridge ? (
                <FunCheckoutButton
                  onBeforeOpen={onClose}
                  source="mobile_drawer"
                  variant="outlined"
                  size="large"
                  sx={{ flex: 1 }}
                />
              ) : null}
            </Stack>
          ) : null}

          {canWithdraw ? (
            <Box px={2}>
              <Button onClick={handleWithdrawClick} variant="outlined" size="large" sx={{ flex: 1, width: '100%' }}>
                {t({ comment: 'Button opening the withdraw dialog', message: 'Withdraw' })}
              </Button>
            </Box>
          ) : null}
        </Stack>

        <ReferralsMiniCard onLinkClick={onClose} />

        {/* The footer's status and links; on the mobile trade page, which has no footer, this is their only home. */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ px: 2, mt: 'auto' }}>
          <ConnectionStatus />
          <Stack direction="row" alignItems="center" gap={1}>
            <SocialMediaLinks docsUrl={features?.docsUrl} twitterUrl={features?.twitterUrl} discordUrl={features?.discordUrl} />
          </Stack>
        </Stack>
      </Stack>
    </BaseDrawer>
  );
});
